import { useEffect, useState } from 'react';
import { Box, Button, GlobalStyles, Portal, Stack, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  canExtend,
  currentPreviewEndsAt,
  endPreview,
  extendPreview,
  getCurrentPreview,
  previewBannerText,
  reloadForPreview,
  setCurrentPreview,
  subscribePreview,
  type RolePreview,
} from '@/auth/rolePreview';

/** Height of the bar; the fixed header, sidebar and filter panel move down by it. */
export const PREVIEW_BANNER_HEIGHT = 36;

/**
 * The persistent warning bar of a role preview (Authorization-Plan §13.7):
 * "Previewing <role> (<organisation>) — changes are saved for real and
 * recorded under your name — ends at HH:MM [Extend 30 minutes] [End preview]",
 * amber (full access, owner decision 2026-10-07); red with "— read only —"
 * when the preview is read only. Ending (or the preview running out) reloads
 * the app with the user's own role.
 */
export const RolePreviewBanner = () => {
  const [preview, setPreview] = useState<RolePreview | null>(getCurrentPreview());
  const [endsAt, setEndsAt] = useState<number | null>(currentPreviewEndsAt());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      subscribePreview((p) => {
        setPreview(p);
        setEndsAt(currentPreviewEndsAt());
      }),
    [],
  );

  // the preview runs out: the server answers the own role again; reload into it
  useEffect(() => {
    if (!preview || endsAt === null) return;
    const timer = setTimeout(
      () => void reloadForPreview(null),
      Math.max(0, endsAt - Date.now()) + 1000,
    );
    return () => clearTimeout(timer);
  }, [preview, endsAt]);

  if (!preview) return null;
  // amber: a preview that saves for real; red: read only
  const tone = preview.read_only ? 'error' : 'warning';

  const end = async () => {
    setBusy(true);
    setError(null);
    const out = await endPreview();
    if (!out.ok) {
      setBusy(false);
      setError(out.message);
      return;
    }
    await reloadForPreview(null);
  };

  const extend = async () => {
    setBusy(true);
    setError(null);
    const out = await extendPreview();
    setBusy(false);
    if (!out.ok) {
      setError(out.message);
      return;
    }
    setCurrentPreview(out.preview);
  };

  return (
    <>
      <GlobalStyles
        styles={{
          body: { paddingTop: PREVIEW_BANNER_HEIGHT },
          '.header__filters, .sidebar__header, #filter_pannel, .MuiDrawer-paper': {
            marginTop: `${PREVIEW_BANNER_HEIGHT}px`,
          },
        }}
      />
      {/* In a portal on <body>: main.scss stretches every `#root > div > div`
          to full height (footer rule), which made the bar cover the page. */}
      <Portal>
        <Box
          role='status'
          aria-live='polite'
          data-testid='role-preview-banner'
          data-mode={preview.read_only ? 'read-only' : 'writable'}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            minHeight: PREVIEW_BANNER_HEIGHT,
            zIndex: 1500,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            bgcolor: `${tone}.main`,
            color: `${tone}.contrastText`,
            boxShadow: 2,
          }}
        >
          <Stack
            direction='row'
            spacing={1.5}
            alignItems='center'
            flexWrap='wrap'
            useFlexGap
            sx={{ width: '100%' }}
          >
            <VisibilityIcon fontSize='small' />
            <Typography
              variant='body2'
              fontWeight={700}
              data-testid='role-preview-text'
              sx={{ flex: '1 1 auto' }}
            >
              {previewBannerText(preview, endsAt ?? Date.now())}
            </Typography>
            {error && (
              <Typography variant='caption' data-testid='role-preview-error'>
                {error}
              </Typography>
            )}
            {canExtend(preview, endsAt) && (
              <Button
                size='small'
                variant='outlined'
                color='inherit'
                disabled={busy}
                onClick={() => void extend()}
                data-testid='role-preview-extend'
              >
                Extend 30 minutes
              </Button>
            )}
            <Button
              size='small'
              variant='contained'
              color='inherit'
              disabled={busy}
              onClick={() => void end()}
              data-testid='role-preview-end'
              sx={{ color: `${tone}.main` }}
            >
              End preview
            </Button>
          </Stack>
        </Box>
      </Portal>
    </>
  );
};

export default RolePreviewBanner;
